import { useState, useEffect } from 'react'
import { usePolicy } from '../../../contexts/PolicyContext'
import { useAuth } from '../../../contexts/AuthContext'
import { supabase } from '../../../shared/lib/supabase'
import { exportPolicyAsDocx, exportStructuredPolicyToPdf } from '../../policies/utils/policyExport'
import GlobalTemplatePolicyPreview from '../components/GlobalTemplatePolicyPreview'
import StructuredPolicyRenderer from '../../../shared/components/StructuredPolicyRenderer'

const DOMAIN_OPTIONS = [
  'Acceptable Use Policy',
  'Access Control Policy',
  'AI & Emerging Technology Policy',
  'Asset Management Policy',
  'Backup & Recovery Policy',
  'Business Continuity & Disaster Recovery Policy',
  'Change Management Policy',
  'Cloud Security Policy',
  'Data Classification & Handling Policy',
  'Data Retention & Disposal Policy',
  'Encryption & Key Management Policy',
  'Endpoint Security Policy',
  'Incident Response Policy',
  'Information Security Policy',
  'Logging, Monitoring & Audit Policy',
  'Mobile Device & BYOD Policy',
  'Network Security Policy',
  'Password & Authentication Policy',
  'Physical Security Policy',
  'Privacy Policy',
  'Remote Work & Telework Policy',
  'Risk Management Policy',
  'Secure Software Development Policy',
  'Security Awareness & Training Policy',
  'Third-Party / Vendor Management Policy',
  'Vulnerability Management Policy'
]

const FRAMEWORK_OPTIONS = [
  'NIST CSF', 'NIST 800-53', 'NIST 800-171', 'HIPAA', 'PCI DSS',
  'SOC 2', 'CMMC', 'ISO 27001', 'GDPR', 'CCPA', 'CIS Controls',
  'FFIEC', 'GLBA', 'FERPA', 'FTC Safeguards Rule', 'NYDFS',
  'SEC Cybersecurity Rules', 'DORA', 'NIST AI RMF', 'ISO 42001',
  'EU AI Act', 'OWASP AI Security', 'MITRE ATLAS', 'FedRAMP',
  'HITRUST CSF', 'COBIT', 'NERC CIP', 'SWIFT CSCF'
]

const STRUCTURE_SCHEMA = {
  document_control: {},
  purpose: '',
  scope: { applies_to: '', exclusions: '' },
  definitions: [],
  policy_statements: [],
  roles_and_responsibilities: [],
  compliance_and_enforcement: '',
  related_documents: []
}

const SECTION_KEYS = [
  { key: 'document_control', label: 'Document Control' },
  { key: 'purpose', label: 'Purpose' },
  { key: 'scope', label: 'Scope' },
  { key: 'definitions', label: 'Definitions' },
  { key: 'policy_statements', label: 'Policy Statements' },
  { key: 'roles_and_responsibilities', label: 'Roles & Responsibilities' },
  { key: 'compliance_and_enforcement', label: 'Compliance & Enforcement' },
  { key: 'related_documents', label: 'Related Documents' }
]

const FLAG_TYPE_LABELS = {
  em_dash: 'Em Dash',
  passive_voice: 'Passive Voice',
  filler_phrase: 'Filler Phrase',
  weak_requirement: 'Weak Requirement',
  non_auditable: 'Non-Auditable',
  parallel_structure: 'Parallel Structure'
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 30) return `${diffDay}d ago`
  return date.toLocaleDateString()
}

function sectionToText(key, value) {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (key === 'document_control') {
    return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join('\n')
  }
  if (key === 'scope') {
    let t = `Applies to: ${value.applies_to || ''}`
    if (value.exclusions) t += `\nExclusions: ${value.exclusions}`
    return t
  }
  if (key === 'definitions') {
    return (value || []).map(d => `${d.term}: ${d.definition}`).join('\n\n')
  }
  if (key === 'policy_statements') {
    return (value || []).map(ps => {
      let t = `[${ps.section_id}] ${ps.title}\n${ps.content}`
      if (ps.bullets?.length) t += '\n' + ps.bullets.map(b => `  - ${b}`).join('\n')
      return t
    }).join('\n\n')
  }
  if (key === 'roles_and_responsibilities') {
    return (value || []).map(r => `${r.role}: ${r.responsibilities}`).join('\n\n')
  }
  if (key === 'related_documents') {
    return (value || []).join('\n')
  }
  return JSON.stringify(value, null, 2)
}

function textToSection(key, text) {
  if (key === 'purpose' || key === 'compliance_and_enforcement') return text
  if (key === 'document_control') {
    const obj = {}
    text.split('\n').forEach(line => {
      const idx = line.indexOf(':')
      if (idx > 0) obj[line.slice(0, idx).trim()] = line.slice(idx + 1).trim()
    })
    return obj
  }
  if (key === 'scope') {
    const lines = text.split('\n')
    const appliesTo = lines.find(l => l.startsWith('Applies to:'))
    const exclusions = lines.find(l => l.startsWith('Exclusions:'))
    return {
      applies_to: appliesTo ? appliesTo.replace('Applies to:', '').trim() : text,
      exclusions: exclusions ? exclusions.replace('Exclusions:', '').trim() : ''
    }
  }
  if (key === 'definitions') {
    return text.split('\n\n').filter(Boolean).map(block => {
      const idx = block.indexOf(':')
      return idx > 0
        ? { term: block.slice(0, idx).trim(), definition: block.slice(idx + 1).trim() }
        : { term: '', definition: block.trim() }
    })
  }
  if (key === 'policy_statements') {
    return text.split('\n\n').filter(Boolean).map(block => {
      const lines = block.split('\n')
      const header = lines[0] || ''
      const match = header.match(/^\[(.+?)\]\s*(.+)$/)
      const bullets = lines.filter(l => l.trim().startsWith('- ')).map(l => l.trim().replace(/^-\s*/, ''))
      const contentLines = lines.slice(1).filter(l => !l.trim().startsWith('- '))
      return {
        section_id: match ? match[1] : '',
        title: match ? match[2] : header,
        content: contentLines.join('\n').trim(),
        bullets
      }
    })
  }
  if (key === 'roles_and_responsibilities') {
    return text.split('\n\n').filter(Boolean).map(block => {
      const idx = block.indexOf(':')
      return idx > 0
        ? { role: block.slice(0, idx).trim(), responsibilities: block.slice(idx + 1).trim() }
        : { role: '', responsibilities: block.trim() }
    })
  }
  if (key === 'related_documents') {
    return text.split('\n').map(l => l.trim()).filter(Boolean)
  }
  return text
}

function structuredToMarkdown(content) {
  if (!content) return ''
  let md = ''
  if (content.document_control) {
    md += '# Document Control\n\n'
    Object.entries(content.document_control).forEach(([k, v]) => {
      md += `**${k}:** ${v}\n`
    })
    md += '\n'
  }
  if (content.purpose) md += `## Purpose\n\n${content.purpose}\n\n`
  if (content.scope) {
    md += `## Scope\n\n**Applies to:** ${content.scope.applies_to}\n`
    if (content.scope.exclusions) md += `**Exclusions:** ${content.scope.exclusions}\n`
    md += '\n'
  }
  if (content.definitions?.length) {
    md += '## Definitions\n\n'
    content.definitions.forEach(d => { md += `**${d.term}:** ${d.definition}\n\n` })
  }
  if (content.policy_statements?.length) {
    md += '## Policy Statements\n\n'
    content.policy_statements.forEach(ps => {
      md += `### ${ps.section_id}: ${ps.title}\n\n${ps.content}\n\n`
      if (ps.bullets?.length) ps.bullets.forEach(b => { md += `- ${b}\n` })
      md += '\n'
    })
  }
  if (content.roles_and_responsibilities?.length) {
    md += '## Roles & Responsibilities\n\n'
    content.roles_and_responsibilities.forEach(r => { md += `**${r.role}:** ${r.responsibilities}\n\n` })
  }
  if (content.compliance_and_enforcement) {
    md += `## Compliance & Enforcement\n\n${content.compliance_and_enforcement}\n\n`
  }
  if (content.related_documents?.length) {
    md += '## Related Documents\n\n'
    content.related_documents.forEach(d => { md += `- ${d}\n` })
  }
  return md
}

// ── Word-level diff ──────────────────────────────────────────────────────────

function computeWordDiff(oldText, newText) {
  const oldWords = (oldText || '').split(/(\s+)/)
  const newWords = (newText || '').split(/(\s+)/)
  const lcs = longestCommonSubsequence(oldWords, newWords)

  const result = []
  let oi = 0, ni = 0, li = 0

  while (oi < oldWords.length || ni < newWords.length) {
    if (li < lcs.length && oi < oldWords.length && ni < newWords.length &&
        oldWords[oi] === lcs[li] && newWords[ni] === lcs[li]) {
      result.push({ type: 'same', text: lcs[li] })
      oi++; ni++; li++
    } else if (li < lcs.length && ni < newWords.length && newWords[ni] === lcs[li]) {
      result.push({ type: 'removed', text: oldWords[oi] || '' })
      oi++
    } else if (li < lcs.length && oi < oldWords.length && oldWords[oi] === lcs[li]) {
      result.push({ type: 'added', text: newWords[ni] || '' })
      ni++
    } else {
      if (oi < oldWords.length) { result.push({ type: 'removed', text: oldWords[oi] }); oi++ }
      if (ni < newWords.length) { result.push({ type: 'added', text: newWords[ni] }); ni++ }
    }
  }
  return result
}

function longestCommonSubsequence(a, b) {
  const m = a.length, n = b.length
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }
  const result = []
  let i = m, j = n
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) { result.unshift(a[i - 1]); i--; j-- }
    else if (dp[i - 1][j] > dp[i][j - 1]) i--
    else j--
  }
  return result
}

// ── StructuredContentPreview — uses shared renderer ─────────────────────────

// ── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ currentStep, steps }) {
  return (
    <div className="flex items-center gap-1 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
      {steps.map((step, i) => {
        const isComplete = i < currentStep
        const isCurrent = i === currentStep
        return (
          <div key={i} className="flex items-center">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              isComplete ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
              isCurrent ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' :
              'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
            }`}>
              {isComplete ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span>{i + 1}</span>
              )}
              {step}
            </div>
            {i < steps.length - 1 && (
              <svg className="w-4 h-4 mx-1 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Diff View ────────────────────────────────────────────────────────────────

function SectionDiffView({ oldContent, newContent, oldLabel, newLabel }) {
  return (
    <div className="space-y-6">
      {SECTION_KEYS.map(({ key, label }) => {
        const oldText = sectionToText(key, oldContent?.[key])
        const newText = sectionToText(key, newContent?.[key])
        if (!oldText && !newText) return null

        const diff = computeWordDiff(oldText, newText)
        const hasChanges = diff.some(d => d.type !== 'same')

        if (!hasChanges) return (
          <div key={key} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{label}</h4>
            <p className="text-xs text-gray-400 italic">No changes</p>
          </div>
        )

        return (
          <div key={key} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{label}</h4>
            </div>
            <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
              <div className="p-4">
                <p className="text-xs text-gray-400 mb-2">{oldLabel}</p>
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                  {diff.map((d, i) => d.type === 'removed' ? (
                    <span key={i} className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 line-through">{d.text}</span>
                  ) : d.type === 'same' ? (
                    <span key={i}>{d.text}</span>
                  ) : null)}
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-gray-400 mb-2">{newLabel}</p>
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                  {diff.map((d, i) => d.type === 'added' ? (
                    <span key={i} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">{d.text}</span>
                  ) : d.type === 'same' ? (
                    <span key={i}>{d.text}</span>
                  ) : null)}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Version History Panel ────────────────────────────────────────────────────

function VersionHistoryPanel({ template, onClose }) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [diffVersion, setDiffVersion] = useState(null)

  useEffect(() => {
    if (!template) return
    const load = async () => {
      setLoading(true)
      const { data } = await supabase
        .from('global_template_versions')
        .select('*, profiles:created_by(full_name, email)')
        .eq('template_id', template.id)
        .order('created_at', { ascending: false })
      setVersions(data || [])
      setLoading(false)
    }
    load()
  }, [template])

  if (diffVersion) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Compare v{diffVersion.version_number} with Current
            </h3>
            <button onClick={() => setDiffVersion(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <SectionDiffView
              oldContent={diffVersion.structured_content}
              newContent={template.structured_content}
              oldLabel={`v${diffVersion.version_number} (${new Date(diffVersion.created_at).toLocaleDateString()})`}
              newLabel={`Current (v${template.version_number || '1.0'})`}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-y-0 right-0 w-[480px] bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col border-l border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{template?.title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Version History</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No version history yet</p>
        ) : (
          <div className="space-y-0">
            {versions.map((v, i) => (
              <div key={v.id} className="relative pl-6 pb-6">
                {i < versions.length - 1 && (
                  <div className="absolute left-[9px] top-6 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
                )}
                <div className="absolute left-0 top-1 w-[18px] h-[18px] rounded-full border-2 border-primary-500 bg-white dark:bg-gray-800" />
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">v{v.version_number}</span>
                    <span className="text-xs text-gray-400">{new Date(v.created_at).toLocaleDateString()}</span>
                  </div>
                  {v.change_summary && <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{v.change_summary}</p>}
                  <p className="text-xs text-gray-400 mb-3">by {v.profiles?.full_name || v.profiles?.email || 'Unknown'}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDiffVersion(v)}
                      className="text-xs px-2.5 py-1 rounded bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                    >
                      Compare with current
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Org Distribution Panel ───────────────────────────────────────────────────

function OrgDistributionPanel({ template, onClose }) {
  const { user } = useAuth()
  const [copies, setCopies] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 20

  useEffect(() => {
    if (!template) return
    const load = async () => {
      setLoading(true)
      const { data } = await supabase
        .from('org_template_copies')
        .select('*, organizations:org_id(name)')
        .eq('global_template_id', template.id)
        .order('updated_at', { ascending: false })
      setCopies(data || [])
      setLoading(false)
    }
    load()
  }, [template])

  const upToDate = copies.filter(c => c.status === 'current').length
  const updateAvailable = copies.filter(c => c.status === 'update_available').length
  const declined = copies.filter(c => c.status === 'declined').length

  const pagedCopies = copies.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  const totalPages = Math.ceil(copies.length / PAGE_SIZE)

  const handleNotify = async (copy) => {
    await supabase.from('activity_log').insert({
      org_id: copy.org_id,
      user_id: user?.id,
      action: 'policy.update_notification',
      resource_type: 'policy_template',
      resource_id: copy.global_template_id,
      metadata: { template_title: template.title, version: template.version_number }
    })
  }

  const handleReOffer = async (copy) => {
    await supabase
      .from('org_template_copies')
      .update({ status: 'update_available', update_declined_at: null, update_declined_by: null, update_available_since: new Date().toISOString() })
      .eq('id', copy.id)
    setCopies(prev => prev.map(c => c.id === copy.id ? { ...c, status: 'update_available' } : c))
  }

  const statusBadge = (status) => {
    if (status === 'current') return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Up to date</span>
    if (status === 'update_available') return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Update available</span>
    return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Declined update</span>
  }

  return (
    <div className="fixed inset-y-0 right-0 w-[600px] bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col border-l border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{template?.title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Organization Distribution</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Summary bar */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex gap-4 text-sm">
        <span className="text-gray-600 dark:text-gray-400">Total: <strong className="text-gray-900 dark:text-white">{copies.length}</strong></span>
        <span className="text-green-600 dark:text-green-400">Up to date: <strong>{upToDate}</strong></span>
        <span className="text-amber-600 dark:text-amber-400">Update available: <strong>{updateAvailable}</strong></span>
        <span className="text-red-600 dark:text-red-400">Declined: <strong>{declined}</strong></span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : copies.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">Not distributed to any organizations yet</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left">Organization</th>
                <th className="px-4 py-3 text-left">Version</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {pagedCopies.map(copy => (
                <tr key={copy.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{copy.organizations?.name || 'Unknown'}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">v{copy.global_version_at_copy}</td>
                  <td className="px-4 py-3">{statusBadge(copy.status)}</td>
                  <td className="px-4 py-3 text-gray-400">{relativeTime(copy.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {copy.status === 'update_available' && (
                      <button onClick={() => handleNotify(copy)} className="text-xs px-2 py-1 rounded text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors">
                        Send notification
                      </button>
                    )}
                    {copy.status === 'declined' && (
                      <button onClick={() => handleReOffer(copy)} className="text-xs px-2 py-1 rounded text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
                        Re-offer update
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} onClick={() => setPage(i)} className={`w-8 h-8 rounded text-xs ${i === page ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Generation Wizard ────────────────────────────────────────────────────────

function GenerationWizard({ onClose, onComplete }) {
  const { user } = useAuth()
  const { createTemplate, fetchGlobalTemplates } = usePolicy()

  // Wizard step: 'configure' | 'review' | 'publish'
  const [wizardStep, setWizardStep] = useState('configure')
  const [wizardGenerating, setWizardGenerating] = useState(false)
  const [generateError, setGenerateError] = useState('')

  // Configure state
  const [policyTitle, setPolicyTitle] = useState('')
  const [policyDomainSelection, setPolicyDomainSelection] = useState('')
  const [customDomain, setCustomDomain] = useState('')
  const policyDomain = policyDomainSelection === '__custom__' ? customDomain : policyDomainSelection
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [customGuidance, setCustomGuidance] = useState('')

  // Review & Edit state
  const [structuredContent, setStructuredContent] = useState(null)
  const [originalContent, setOriginalContent] = useState(null)
  const [qualityFlags, setQualityFlags] = useState([])
  const [activeSection, setActiveSection] = useState('document_control')
  const [sectionTexts, setSectionTexts] = useState({})

  // Publish state
  const [changeSummary, setChangeSummary] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [publishSuccess, setPublishSuccess] = useState(false)
  const [publishError, setPublishError] = useState('')
  const [activeOrgCount, setActiveOrgCount] = useState(0)

  // Load active org count for publish
  useEffect(() => {
    supabase.from('organizations').select('id', { count: 'exact', head: true }).eq('status', 'active')
      .then(({ count }) => setActiveOrgCount(count || 0))
  }, [])

  // Fetch policy settings for generation
  const fetchPolicySettings = async () => {
    const { data } = await supabase.from('policy_settings').select('*').eq('user_id', user.id).maybeSingle()
    if (!data) return { firm_name: 'Sidewalk Security Advisors', requirement_verb: 'shall', recommendation_verb: 'should' }
    return {
      firm_name: data.firm_name || 'Sidewalk Security Advisors',
      standard_disclaimer: data.standard_disclaimer || '',
      language_preferences: data.language_preferences || {},
      default_review_cycle: data.default_review_cycle || 'Annual'
    }
  }

  // Generate handler — single call to generate_structured + quality scan
  const handleGenerate = async () => {
    if (!policyTitle.trim() || !policyDomain) return
    setWizardGenerating(true)
    setGenerateError('')

    try {
      const settings = await fetchPolicySettings()

      const genRes = await fetch('/api/ai/policy-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'generate_structured',
          policy_title: policyTitle.trim(),
          policy_domain: policyDomain,
          custom_guidance: customGuidance.trim() || undefined,
          policy_settings: settings
        })
      })

      if (!genRes.ok) {
        const err = await genRes.json().catch(() => ({}))
        setGenerateError(err.error || 'Generation failed. Please try again.')
        return
      }

      const genData = await genRes.json()
      if (!genData.structured_content) {
        setGenerateError('Generation returned no content. Please try again.')
        return
      }

      setStructuredContent(genData.structured_content)
      setOriginalContent(JSON.parse(JSON.stringify(genData.structured_content)))

      const texts = {}
      SECTION_KEYS.forEach(({ key }) => {
        texts[key] = sectionToText(key, genData.structured_content[key])
      })
      setSectionTexts(texts)

      // Quality scan (non-blocking on failure)
      try {
        const flagRes = await fetch('/api/ai/policy-generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'flag_quality', structured_content: genData.structured_content })
        })
        if (flagRes.ok) {
          const flagData = await flagRes.json()
          const flags = flagData.quality_flags || flagData.flags || []
          setQualityFlags(flags.map((f, i) => ({ ...f, id: i, acknowledged: false })))
        }
      } catch {
        setQualityFlags([])
      }

      setWizardStep('review')
    } catch (err) {
      setGenerateError(err.message || 'An unexpected error occurred. Please try again.')
    } finally {
      setWizardGenerating(false)
    }
  }

  // Review: Apply quality suggestion
  const applySuggestion = (flag) => {
    if (!flag.suggested || !flag.original) return
    // Try to find and replace the original text in the relevant section content
    const updatedContent = { ...structuredContent }
    const contentStr = JSON.stringify(updatedContent)
    const replaced = contentStr.replace(flag.original, flag.suggested)
    try {
      const parsed = JSON.parse(replaced)
      setStructuredContent(parsed)
      // Update section texts
      const texts = {}
      SECTION_KEYS.forEach(({ key }) => {
        texts[key] = sectionToText(key, parsed[key])
      })
      setSectionTexts(texts)
    } catch { /* ignore parse errors */ }
    setQualityFlags(prev => prev.map(f => f.id === flag.id ? { ...f, acknowledged: true } : f))
  }

  const dismissFlag = (flagId) => {
    setQualityFlags(prev => prev.map(f => f.id === flagId ? { ...f, acknowledged: true } : f))
  }

  const handleSectionTextChange = (key, text) => {
    setSectionTexts(prev => ({ ...prev, [key]: text }))
    const parsed = textToSection(key, text)
    setStructuredContent(prev => ({ ...prev, [key]: parsed }))
  }

  const resetSection = (key) => {
    if (!originalContent) return
    setSectionTexts(prev => ({ ...prev, [key]: sectionToText(key, originalContent[key]) }))
    setStructuredContent(prev => ({ ...prev, [key]: JSON.parse(JSON.stringify(originalContent[key])) }))
  }

  // Extract framework tags from generated content's related_documents
  // Framework tags are empty at Tier 1 — populated during Tier 3 client enrichment
  const extractFrameworkTags = () => []

  // Save as draft
  const saveDraft = async () => {
    try {
      await createTemplate({
        title: policyTitle,
        domain: policyDomain,
        content: structuredToMarkdown(structuredContent),
        framework_tags: extractFrameworkTags(),
        status: 'draft',
        global_template: true,
        structured_content: structuredContent,
        generation_model: 'claude-opus-4-6',
        quality_flags: qualityFlags,
        version_number: '1.0'
      })
      onClose()
      onComplete()
    } catch (err) {
      setPublishError(err.message)
    }
  }

  // Publish
  const handlePublish = async () => {
    if (!changeSummary.trim()) {
      setPublishError('Please provide a change summary')
      return
    }
    setPublishing(true)
    setPublishError('')

    try {
      const content = structuredToMarkdown(structuredContent)
      const frameworkTags = extractFrameworkTags()

      const { data: newTemplate, error: insertErr } = await supabase
        .from('policy_templates')
        .insert({
          title: policyTitle,
          domain: policyDomain,
          content,
          framework_tags: frameworkTags,
          status: 'draft',
          global_template: true,
          structured_content: structuredContent,
          generation_model: 'claude-opus-4-6',
          quality_flags: qualityFlags,
          version_number: '1.0',
          change_summary: changeSummary,
          user_id: user.id
        })
        .select()
        .single()

      if (insertErr) throw insertErr

      await supabase.from('global_template_versions').insert({
        template_id: newTemplate.id,
        version_number: '1.0',
        structured_content: structuredContent,
        content,
        change_summary: changeSummary,
        quality_flags: qualityFlags,
        created_by: user.id
      })

      setPublishSuccess(true)
      await fetchGlobalTemplates()
    } catch (err) {
      setPublishError(err.message || 'Publishing failed')
    } finally {
      setPublishing(false)
    }
  }

  const warningCount = qualityFlags.filter(f => f.severity === 'warning' && !f.acknowledged).length
  const infoCount = qualityFlags.filter(f => f.severity === 'info' && !f.acknowledged).length
  const addressedCount = qualityFlags.filter(f => f.acknowledged).length
  const dismissedCount = qualityFlags.filter(f => f.acknowledged && f.flag_type).length

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Generate Global Template</h2>
          {!wizardGenerating && !publishing && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="flex items-center px-6 py-2.5 border-b border-gray-200 dark:border-gray-700">
          {[
            { id: 'configure', label: 'Configure', num: 1 },
            { id: 'review', label: 'Review & Save', num: 2 }
          ].map((s, i, arr) => {
            const stepOrder = ['configure', 'review']
            const currentIdx = stepOrder.indexOf(wizardStep)
            const thisIdx = stepOrder.indexOf(s.id)
            const isComplete = thisIdx < currentIdx || (s.id === 'publish' && publishSuccess)
            const isCurrent = s.id === wizardStep && !publishSuccess
            return (
              <div key={s.id} className="flex items-center" style={{ flex: i < arr.length - 1 ? 1 : undefined }}>
                <div className={`flex items-center gap-1.5 text-xs font-medium whitespace-nowrap ${isCurrent ? 'text-primary-600 dark:text-primary-400' : isComplete ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isCurrent ? 'bg-primary-600 text-white' : isComplete ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {isComplete ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    ) : s.num}
                  </span>
                  {s.label}
                </div>
                {i < arr.length - 1 && <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700 mx-3" />}
              </div>
            )
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* ── Configure Step ── */}
          {wizardStep === 'configure' && (
            <div className="flex gap-6">
              {/* Left: Configure fields */}
              <div className="w-[60%] space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Policy Domain</label>
                  <select
                    value={policyDomainSelection}
                    onChange={e => {
                      setPolicyDomainSelection(e.target.value)
                      if (e.target.value !== '__custom__') {
                        setCustomDomain('')
                        setPolicyTitle(e.target.value)
                      } else {
                        setPolicyTitle('')
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select domain...</option>
                    {DOMAIN_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    <option value="__custom__">Custom Domain...</option>
                  </select>
                  {policyDomainSelection === '__custom__' && (
                    <input
                      type="text"
                      value={customDomain}
                      onChange={e => { setCustomDomain(e.target.value); setPolicyTitle(e.target.value) }}
                      placeholder="e.g., AI Governance Policy, Cloud Security Policy"
                      className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                    The policy title is set automatically from the domain. You can rename it after generation.
                  </p>
                </div>
                {/* Advanced Options */}
                <div>
                  <button type="button" onClick={() => setShowAdvancedOptions(prev => !prev)} className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    <svg className={`w-4 h-4 transition-transform ${showAdvancedOptions ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                    Advanced options
                  </button>
                  {showAdvancedOptions && (
                    <div className="mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wider">Custom Guidance</label>
                        <textarea
                          value={customGuidance}
                          onChange={(e) => setCustomGuidance(e.target.value)}
                          rows={3}
                          placeholder="Provide additional governance requirements for the AI. Example: Include specific AI governance controls addressing model risk management, algorithmic bias monitoring, and automated decision-making transparency requirements."
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        />
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                          Free-text instructions for additional governance requirements. Framework and regulatory citations are added during client-specific enrichment.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {generateError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <span>{generateError}</span>
                  </div>
                )}
              </div>

              {/* Right: Info panel */}
              <div className="w-[40%]">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 h-full">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    How this works
                  </h4>
                  <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                    <p>Claude Opus generates clean, auditable governance language with no framework citations. This template serves as the universal baseline for all client engagements.</p>
                    <p>Framework references, regulatory citations, and control mappings are added during <span className="font-medium text-gray-700 dark:text-gray-300">client-specific enrichment</span> based on each client's actual industry, regulatory scope, and assessment data.</p>
                    <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Template approach</p>
                      <div className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-start gap-2">
                          <svg className="w-3.5 h-3.5 text-primary-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
                          <span><span className="font-medium text-gray-700 dark:text-gray-300">Governance first</span> — strong, auditable policy language that stands on its own</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-3.5 h-3.5 text-primary-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
                          <span><span className="font-medium text-gray-700 dark:text-gray-300">Industry-agnostic</span> — no framework citations baked into the template</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <svg className="w-3.5 h-3.5 text-primary-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
                          <span><span className="font-medium text-gray-700 dark:text-gray-300">Client enrichment</span> — frameworks and controls layered in per client's regulatory scope</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Review & Save Step ── */}
          {wizardStep === 'review' && (
            <div>
              {/* Editable title */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Policy Title</label>
                <input
                  type="text"
                  value={policyTitle}
                  onChange={e => setPolicyTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-medium"
                />
              </div>

            <div className="flex gap-6 h-full min-h-[500px]">
              {/* Left: Quality flags */}
              <div className="w-[40%] space-y-3 overflow-y-auto pr-2">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Quality Review</h3>
                  {warningCount > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">{warningCount} warnings</span>}
                  {infoCount > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{infoCount} info</span>}
                </div>

                {qualityFlags.filter(f => !f.acknowledged).length === 0 ? (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-center">
                    <svg className="w-8 h-8 text-green-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                      {qualityFlags.length === 0 ? 'No quality issues found. This policy is ready to publish.' : 'All items addressed.'}
                    </p>
                  </div>
                ) : (
                  qualityFlags.filter(f => !f.acknowledged).map(flag => (
                    <div key={flag.id} className="bg-white dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          flag.severity === 'warning'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                        }`}>
                          {FLAG_TYPE_LABELS[flag.flag_type] || flag.flag_type}
                        </span>
                        {flag.section && <span className="text-xs text-gray-400">{flag.section}</span>}
                      </div>
                      {flag.original && (
                        <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-400 mb-2">
                          <span className="bg-amber-100 dark:bg-amber-900/30 px-0.5">{flag.original}</span>
                        </div>
                      )}
                      {flag.suggested && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                          <span className="font-medium">Suggested:</span> {flag.suggested}
                        </p>
                      )}
                      <div className="flex gap-2">
                        {flag.suggested && (
                          <button
                            onClick={() => applySuggestion(flag)}
                            className="text-xs px-2 py-1 rounded bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                          >
                            Apply suggestion
                          </button>
                        )}
                        <button
                          onClick={() => dismissFlag(flag.id)}
                          className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Right: Section editor */}
              <div className="w-[60%] flex gap-3">
                {/* Section tabs */}
                <div className="w-44 flex-shrink-0 space-y-1">
                  {SECTION_KEYS.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setActiveSection(key)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        activeSection === key
                          ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Editor */}
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {SECTION_KEYS.find(s => s.key === activeSection)?.label}
                    </h4>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">{(sectionTexts[activeSection] || '').split(/\s+/).filter(Boolean).length} words</span>
                      <button
                        onClick={() => resetSection(activeSection)}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Reset to generated
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={sectionTexts[activeSection] || ''}
                    onChange={e => handleSectionTextChange(activeSection, e.target.value)}
                    className="flex-1 w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    style={{ minHeight: '400px' }}
                  />
                </div>
              </div>
            </div>
              {/* Save fields — inline at bottom of review */}
              <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700">
                <div className="max-w-xl space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      What does this template cover? <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={changeSummary}
                      onChange={e => setChangeSummary(e.target.value)}
                      placeholder="e.g., Comprehensive access control governance covering identity management, authentication, authorization, and privileged access requirements"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  </div>
                  {publishError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">{publishError}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Success state */}
          {publishSuccess && (
            <div className="max-w-xl mx-auto text-center py-12">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Template Saved</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Template saved to the Global Library. Review, download, and distribute to organizations from the template card.
              </p>
              <button onClick={() => { onClose(); onComplete() }} className="btn-primary">Done</button>
            </div>
          )}

        </div>

        {/* Footer */}
        {!publishSuccess && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            {wizardStep === 'configure' && (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400">Generation typically takes 30-60 seconds.</p>
                <button
                  onClick={handleGenerate}
                  disabled={!policyTitle.trim() || !policyDomain || wizardGenerating}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {wizardGenerating ? (
                    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Generating...</>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                      </svg>
                      Generate Policy
                    </>
                  )}
                </button>
              </>
            )}
            {wizardStep === 'review' && (
              <>
                <div className="flex items-center gap-3">
                  <button onClick={() => setWizardStep('configure')} className="btn-secondary text-sm">{'\u2190'} Back</button>
                  <button onClick={saveDraft} className="btn-secondary text-sm">Save as Draft</button>
                </div>
                <button
                  onClick={handlePublish}
                  disabled={publishing || !changeSummary.trim()}
                  className="btn-primary disabled:opacity-50 flex items-center gap-2"
                >
                  {publishing ? (
                    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Saving...</>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Save to Library
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

function GlobalPolicyTemplates() {
  const { globalTemplates, deleteTemplate, fetchGlobalTemplates } = usePolicy()
  const { user } = useAuth()

  // Modal/panel state
  const [showWizard, setShowWizard] = useState(false)
  const [viewingTemplate, setViewingTemplate] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [historyTemplate, setHistoryTemplate] = useState(null)
  const [distTemplate, setDistTemplate] = useState(null)

  // Preview & upload state
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [previewVersionHistory, setPreviewVersionHistory] = useState([])
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [showUploadRevision, setShowUploadRevision] = useState(false)
  const [downloadDropdownId, setDownloadDropdownId] = useState(null)
  const [uploadRevisionTemplate, setUploadRevisionTemplate] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadChangeSummary, setUploadChangeSummary] = useState('')
  const [uploading, setUploading] = useState(false)

  // Org distribution counts per template
  const [distCounts, setDistCounts] = useState({})

  useEffect(() => {
    if (!globalTemplates.length) return
    const loadCounts = async () => {
      const counts = {}
      for (const gt of globalTemplates) {
        const { data } = await supabase
          .from('org_template_copies')
          .select('status')
          .eq('global_template_id', gt.id)
        if (data) {
          counts[gt.id] = {
            total: data.length,
            current: data.filter(c => c.status === 'current').length,
            updateAvailable: data.filter(c => c.status === 'update_available').length
          }
        }
      }
      setDistCounts(counts)
    }
    loadCounts()
  }, [globalTemplates])

  const handleOpenPreview = async (template) => {
    setLoadingPreview(true)
    try {
      const { data: versions } = await supabase
        .from('global_template_versions')
        .select('version_number, created_at, change_summary, created_by')
        .eq('template_id', template.id)
        .order('created_at', { ascending: false })
      setPreviewVersionHistory(versions || [])
    } catch {
      setPreviewVersionHistory([])
    } finally {
      setLoadingPreview(false)
    }
    setPreviewTemplate(template)
  }

  const handleDownloadDocx = async (template) => {
    try {
      // Fetch version history for revision table
      let versions = previewVersionHistory || []
      if (versions.length === 0) {
        const { data } = await supabase
          .from('global_template_versions')
          .select('version_number, created_at, change_summary, created_by')
          .eq('template_id', template.id)
          .order('created_at', { ascending: false })
        versions = (data || []).map(v => ({ ...v, created_by_name: 'SSA Advisory Team' }))
      }
      await exportPolicyAsDocx(
        template,
        versions,
        { name: 'Sidewalk Security Advisors', industry: '' },
        null,
        'Sidewalk Security Advisors'
      )
    } catch (err) {
      console.error('DOCX export failed:', err)
    }
  }

  const handleDownloadPdf = async () => {
    try {
      await exportStructuredPolicyToPdf(
        previewTemplate,
        previewVersionHistory,
        { name: 'Sidewalk Security Advisors' },
        null,
        'Sidewalk Security Advisors'
      )
    } catch (err) {
      console.error('PDF export failed:', err)
    }
  }

  const handleUploadRevision = async () => {
    if (!uploadFile || !uploadChangeSummary.trim() || !uploadRevisionTemplate) return
    setUploading(true)
    try {
      // Note: Requires 'global-templates' bucket in Supabase Storage (create manually in dashboard)
      const filePath = `global-templates/${uploadRevisionTemplate.id}/${Date.now()}.docx`
      const { error: storageError } = await supabase.storage.from('global-templates').upload(filePath, uploadFile)

      if (storageError) {
        if (storageError.message?.includes('bucket') || storageError.message?.includes('not found')) {
          alert('The "global-templates" storage bucket has not been created yet. Create it in Supabase Storage before uploading revisions.')
          return
        }
        throw storageError
      }

      const currentVersion = uploadRevisionTemplate.version_number || '1.0'
      const [major, minor] = currentVersion.split('.').map(Number)
      const newVersion = `${major}.${(minor || 0) + 1}`

      await supabase.from('global_template_versions').insert({
        template_id: uploadRevisionTemplate.id,
        version_number: newVersion,
        structured_content: uploadRevisionTemplate.structured_content || {},
        content: uploadRevisionTemplate.content || '',
        change_summary: uploadChangeSummary.trim(),
        created_by: user?.id
      })

      await supabase.from('policy_templates').update({ version_number: newVersion }).eq('id', uploadRevisionTemplate.id)

      await supabase.from('org_template_copies')
        .update({ status: 'update_available', update_available_since: new Date().toISOString() })
        .eq('global_template_id', uploadRevisionTemplate.id)
        .eq('status', 'current')

      setShowUploadRevision(false)
      setUploadRevisionTemplate(null)
      setUploadFile(null)
      setUploadChangeSummary('')
      await fetchGlobalTemplates()
    } catch (err) {
      console.error('Upload revision error:', err)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteTemplate(id)
      setShowDeleteConfirm(null)
      await fetchGlobalTemplates()
    } catch (err) {
      console.error('Error deleting global template:', err)
    }
  }

  // Distribute a published template to all active orgs (first-time)
  const [distributing, setDistributing] = useState(null)

  const handleDistributeToOrgs = async (template) => {
    setDistributing(template.id)
    try {
      // Mark the global template as published
      await supabase.from('policy_templates').update({
        status: 'published',
        published_at: new Date().toISOString(),
        published_by: user.id
      }).eq('id', template.id)

      const { data: orgs } = await supabase.from('organizations').select('id').eq('status', 'active')
      if (!orgs?.length) { setDistributing(null); await fetchGlobalTemplates(); return }

      for (const org of orgs) {
        // Skip if this org already has a copy of this global template
        const { data: existingCopy } = await supabase
          .from('org_template_copies')
          .select('id')
          .eq('org_id', org.id)
          .eq('global_template_id', template.id)
          .limit(1)

        if (existingCopy?.length) continue

        const { data: admins } = await supabase
          .from('org_members')
          .select('user_id')
          .eq('org_id', org.id)
          .eq('role', 'org_admin')
          .limit(1)

        const ownerId = admins?.[0]?.user_id || user.id

        const { data: orgTemplate } = await supabase
          .from('policy_templates')
          .insert({
            title: template.title,
            domain: template.domain,
            content: template.content || '',
            framework_tags: template.framework_tags || [],
            status: 'published',
            global_template: false,
            structured_content: template.structured_content,
            generation_model: template.generation_model,
            version_number: template.version_number || '1.0',
            source_global_template_id: template.id,
            user_id: ownerId
          })
          .select('id')
          .single()

        await supabase.from('org_template_copies').upsert({
          org_id: org.id,
          global_template_id: template.id,
          org_template_id: orgTemplate?.id || null,
          global_version_at_copy: template.version_number || '1.0',
          status: 'current'
        }, { onConflict: 'org_id,global_template_id' })
      }

      await fetchGlobalTemplates()
    } catch (err) {
      console.error('Distribution failed:', err)
    } finally {
      setDistributing(null)
    }
  }

  // Push an update to orgs that already have copies of this template
  const [pushingUpdate, setPushingUpdate] = useState(null)

  const handlePushUpdate = async (template) => {
    setPushingUpdate(template.id)
    try {
      await supabase.from('org_template_copies')
        .update({
          status: 'update_available',
          update_available_since: new Date().toISOString()
        })
        .eq('global_template_id', template.id)
        .eq('status', 'current')

      await fetchGlobalTemplates()
    } catch (err) {
      console.error('Push update failed:', err)
    } finally {
      setPushingUpdate(null)
    }
  }

  // Check if a template has been distributed (has any org_template_copies records)
  // Search and filter
  const [search, setSearch] = useState('')
  const [domainFilter, setDomainFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [frameworkFilters, setFrameworkFilters] = useState([])
  const [showFrameworkDropdown, setShowFrameworkDropdown] = useState(false)

  // Close dropdowns on outside click
  useEffect(() => {
    if (!showFrameworkDropdown && !downloadDropdownId) return
    const handleClick = (e) => {
      if (!e.target.closest('[data-fw-dropdown]')) setShowFrameworkDropdown(false)
      if (!e.target.closest('[data-dl-dropdown]')) setDownloadDropdownId(null)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showFrameworkDropdown, downloadDropdownId])

  const isDistributed = (templateId) => {
    const dc = distCounts[templateId]
    return dc && dc.total > 0
  }

  const filtered = globalTemplates.filter(gt => {
    if (search) {
      const q = search.toLowerCase()
      if (!gt.title?.toLowerCase().includes(q) && !gt.domain?.toLowerCase().includes(q)) return false
    }
    if (domainFilter && gt.domain !== domainFilter) return false
    if (statusFilter && gt.status !== statusFilter) return false
    if (frameworkFilters.length > 0) {
      const tags = gt.framework_tags || []
      if (!frameworkFilters.some(f => tags.includes(f))) return false
    }
    return true
  })

  const domains = [...new Set(globalTemplates.map(gt => gt.domain).filter(Boolean))].sort()
  const allFrameworks = [...new Set(globalTemplates.flatMap(gt => gt.framework_tags || []))].sort()

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Policy Templates</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Global templates available to all organizations
          </p>
        </div>
        <button onClick={() => setShowWizard(true)} className="btn-primary flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
          </svg>
          Generate Global Template
        </button>
      </div>

      {/* Metric cards */}
      {(() => {
        const totalTemplates = globalTemplates.length
        const drafts = globalTemplates.filter(gt => gt.status === 'draft').length
        const published = globalTemplates.filter(gt => gt.status === 'published').length
        const totalOrgCopies = Object.values(distCounts).reduce((sum, dc) => sum + (dc?.total || 0), 0)
        const pendingUpdates = Object.values(distCounts).reduce((sum, dc) => sum + (dc?.updateAvailable || 0), 0)
        const uniqueFrameworks = new Set(globalTemplates.flatMap(gt => gt.framework_tags || [])).size
        const uniqueDomains = new Set(globalTemplates.map(gt => gt.domain).filter(Boolean)).size

        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{totalTemplates}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Drafts</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">{drafts}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Published</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{published}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{totalOrgCopies} org copies</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pending Updates</p>
              <p className={`text-2xl font-bold mt-1 ${pendingUpdates > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-300 dark:text-gray-600'}`}>{pendingUpdates}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Coverage</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{uniqueDomains}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{uniqueFrameworks} frameworks</p>
            </div>
          </div>
        )
      })()}

      {/* Search and filter bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by title or domain..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>
          <select value={domainFilter} onChange={e => setDomainFilter(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm">
            <option value="">All Domains</option>
            {domains.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm min-w-[140px]">
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          {/* Framework multi-select */}
          <div className="relative" data-fw-dropdown>
            <button
              onClick={() => setShowFrameworkDropdown(!showFrameworkDropdown)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm flex items-center gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              {frameworkFilters.length === 0 ? 'All Frameworks' : `${frameworkFilters.length} Framework${frameworkFilters.length > 1 ? 's' : ''}`}
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showFrameworkDropdown && (
              <div className="absolute z-20 mt-1 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 max-h-64 overflow-y-auto">
                {frameworkFilters.length > 0 && (
                  <button
                    onClick={() => setFrameworkFilters([])}
                    className="w-full text-left px-3 py-1.5 text-xs text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 mb-1"
                  >
                    Clear selection
                  </button>
                )}
                {allFrameworks.map(fw => (
                  <label key={fw} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={frameworkFilters.includes(fw)}
                      onChange={() => setFrameworkFilters(prev => prev.includes(fw) ? prev.filter(f => f !== fw) : [...prev, fw])}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    {fw}
                  </label>
                ))}
                {allFrameworks.length === 0 && (
                  <p className="px-3 py-2 text-xs text-gray-400">No frameworks found</p>
                )}
              </div>
            )}
          </div>
          {(search || domainFilter || statusFilter || frameworkFilters.length > 0) && (
            <button onClick={() => { setSearch(''); setDomainFilter(''); setStatusFilter(''); setFrameworkFilters([]); setShowFrameworkDropdown(false) }} className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Template count */}
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Showing {filtered.length} of {globalTemplates.length} template{globalTemplates.length !== 1 ? 's' : ''}
      </p>

      {/* Template table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {globalTemplates.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 mb-2">No global templates yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">Generate structured templates with AI (Opus) that can be used across all organizations</p>
            <button onClick={() => setShowWizard(true)} className="btn-primary">Generate Global Template</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">No templates match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Domain</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Version</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Frameworks</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Distribution</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Updated</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(gt => {
                  const dc = distCounts[gt.id]
                  return (
                    <tr key={gt.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white max-w-[220px] truncate">{gt.title}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-[180px] truncate">{gt.domain}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                          gt.status === 'published'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {gt.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">v{gt.version_number || '1.0'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(gt.framework_tags || []).slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{tag}</span>
                          ))}
                          {(gt.framework_tags || []).length > 3 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">+{gt.framework_tags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {dc && dc.total > 0 ? (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{dc.total} org{dc.total !== 1 ? 's' : ''}</span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">Not distributed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{relativeTime(gt.updated_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Preview */}
                          <button onClick={() => handleOpenPreview(gt)} disabled={loadingPreview} className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Preview">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </button>
                          {/* Download dropdown */}
                          <div className="relative" data-dl-dropdown>
                            <button onClick={() => setDownloadDropdownId(downloadDropdownId === gt.id ? null : gt.id)} className="p-1 rounded text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Download">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                            </button>
                            {downloadDropdownId === gt.id && (
                              <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                                <button
                                  onClick={() => { setDownloadDropdownId(null); handleDownloadDocx(gt) }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                  Download .docx
                                </button>
                                <button
                                  onClick={async () => {
                                    setDownloadDropdownId(null)
                                    await handleOpenPreview(gt)
                                    setTimeout(() => handleDownloadPdf(), 500)
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                  Download .pdf
                                </button>
                              </div>
                            )}
                          </div>
                          {/* Upload revision */}
                          <button onClick={() => { setUploadRevisionTemplate(gt); setShowUploadRevision(true); setUploadFile(null); setUploadChangeSummary('') }} className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Upload revision">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
                          </button>
                          {/* Publish / Published indicator / Push Update */}
                          {gt.status !== 'published' && (
                            <button onClick={() => handleDistributeToOrgs(gt)} disabled={distributing === gt.id} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors" title="Publish to organizations">
                              {distributing === gt.id ? <span className="animate-spin inline-block w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full" /> : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                              Publish
                            </button>
                          )}
                          {gt.status === 'published' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 dark:text-green-400" title="Published to organizations">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </span>
                          )}
                          {/* Push Update — only shown when a revision has been uploaded (version bumped beyond what orgs have) */}
                          {gt.status === 'published' && isDistributed(gt.id) && dc && dc.updateAvailable > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400" title={`${dc.updateAvailable} org(s) have update available`}>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
                              Update Pending
                            </span>
                          )}
                          {/* History */}
                          <button onClick={() => setHistoryTemplate(gt)} className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="History">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          </button>
                          {/* Distribution status */}
                          <button onClick={() => setDistTemplate(gt)} className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Distribution status">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                          </button>
                          {/* Delete */}
                          <button onClick={() => setShowDeleteConfirm(gt)} className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Delete">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">{viewingTemplate.title}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{viewingTemplate.domain}</p>
              </div>
              <button onClick={() => setViewingTemplate(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-white shadow-lg max-w-[750px] mx-auto" style={{ minHeight: '600px' }}>
                <StructuredPolicyRenderer structuredContent={viewingTemplate.structured_content} template={viewingTemplate} showRevisionHistory={false} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Global Template</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Delete <span className="font-medium">&ldquo;{showDeleteConfirm.title}&rdquo;</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="btn-secondary">Cancel</button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generation Wizard */}
      {showWizard && (
        <GenerationWizard
          onClose={() => setShowWizard(false)}
          onComplete={() => fetchGlobalTemplates()}
        />
      )}

      {/* Version History Panel */}
      {historyTemplate && (
        <VersionHistoryPanel
          template={historyTemplate}
          onClose={() => setHistoryTemplate(null)}
        />
      )}

      {/* Org Distribution Panel */}
      {distTemplate && (
        <OrgDistributionPanel
          template={distTemplate}
          onClose={() => setDistTemplate(null)}
        />
      )}

      {/* Document Preview Modal */}
      {previewTemplate && (
        <GlobalTemplatePolicyPreview
          template={previewTemplate}
          versionHistory={previewVersionHistory}
          onClose={() => { setPreviewTemplate(null); setPreviewVersionHistory([]) }}
          onDownloadDocx={() => handleDownloadDocx(previewTemplate)}
          onDownloadPdf={handleDownloadPdf}
        />
      )}

      {/* Upload Revision Modal */}
      {showUploadRevision && uploadRevisionTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Revised Template</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{uploadRevisionTemplate.title}</p>
              </div>
              <button onClick={() => { setShowUploadRevision(false); setUploadRevisionTemplate(null); setUploadFile(null); setUploadChangeSummary('') }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Upload a revised .docx file. All organizations with copies will be notified of the update.
            </p>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center mb-4 cursor-pointer transition-colors ${uploadFile ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'}`}
              onClick={() => document.getElementById('global-upload-input').click()}
            >
              <input id="global-upload-input" type="file" accept=".docx" className="hidden" onChange={e => setUploadFile(e.target.files[0] || null)} />
              {uploadFile ? (
                <div>
                  <p className="text-sm font-medium text-primary-700 dark:text-primary-300">{uploadFile.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <div>
                  <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Drop your .docx file here or click to browse</p>
                </div>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Change summary <span className="text-red-500">*</span></label>
              <textarea value={uploadChangeSummary} onChange={e => setUploadChangeSummary(e.target.value)} rows={3} placeholder="Describe what changed in this revision..." className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none" />
            </div>
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-xs text-amber-700 dark:text-amber-300">All organizations with copies of this template will see an "Update available" notification.</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowUploadRevision(false); setUploadRevisionTemplate(null); setUploadFile(null); setUploadChangeSummary('') }} className="btn-secondary">Cancel</button>
              <button onClick={handleUploadRevision} disabled={!uploadFile || !uploadChangeSummary.trim() || uploading} className="btn-primary disabled:opacity-50">
                {uploading ? 'Uploading...' : 'Upload & Notify Orgs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GlobalPolicyTemplates
